import { Shield, Award, Clock, Check, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Pill } from '../_primitives';

export type TrustTone = 'success' | 'warning' | 'danger';

export interface TrustSignal {
  id: 'reality' | 'authority' | 'temporal';
  label: string;
  status: string;
  /** Compact metric, e.g. "24/24" or "2026". */
  value: string;
  tone: TrustTone;
}

export interface TrustSignalsCardProps {
  signals: TrustSignal[];
  /** Aggregate completion, e.g. "2/3". When omitted, derived from signals. */
  summary?: string;
}

const ICON: Record<TrustSignal['id'], LucideIcon> = {
  reality: Shield,
  authority: Award,
  temporal: Clock,
};

const TONE_BG: Record<TrustTone, string> = {
  success: 'bg-teal-light text-teal-dark',
  warning: 'bg-[hsl(var(--status-warning-bg))] text-[hsl(var(--status-warning))]',
  danger: 'bg-[hsl(var(--status-error-bg))] text-[hsl(var(--status-error))]',
};

const TONE_VALUE: Record<TrustTone, string> = {
  success: 'text-[hsl(var(--status-success))]',
  warning: 'text-[hsl(var(--status-warning))]',
  danger: 'text-[hsl(var(--status-error))]',
};

export function TrustSignalsCard({ signals, summary }: TrustSignalsCardProps) {
  const okCount = signals.filter((s) => s.tone === 'success').length;
  const hasSignals = signals.length > 0;
  const aggregate = summary ?? (hasSignals ? `${okCount}/${signals.length}` : 'Not evaluated');

  return (
    <div
      data-rail-section="trust"
      className="bg-card border border-border rounded-lg overflow-hidden"
    >
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border bg-warm-bg/60">
        <Shield className="size-3.5 text-teal-dark" />
        <div className="text-[12px] font-bold text-foreground">Trust signals</div>
        <div className="flex-1" />
        <Pill tone={hasSignals ? 'success' : 'neutral'} size="sm" icon={hasSignals ? <Check className="size-3" /> : undefined}>
          {aggregate}
        </Pill>
      </div>
      {!hasSignals && (
        <div className="px-3 py-4 text-center text-[12px] text-muted-foreground">
          Trust analysis has not run for this article.
        </div>
      )}
      {signals.map((s, i) => {
        const Icon = ICON[s.id];
        return (
          <div
            key={s.id}
            className={cn(
              'flex items-center gap-2.5 px-3 py-2.5',
              i > 0 && 'border-t border-border',
            )}
          >
            <div
              className={cn(
                'grid place-items-center size-7 rounded-md shrink-0',
                TONE_BG[s.tone],
              )}
            >
              <Icon className="size-3.5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-semibold text-foreground">{s.label}</div>
              <div className="text-[11.5px] text-muted-foreground mt-0.5 truncate">{s.status}</div>
            </div>
            <div className={cn('text-[11.5px] font-bold font-mono', TONE_VALUE[s.tone])}>
              {s.value}
            </div>
          </div>
        );
      })}
    </div>
  );
}
